import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner 
} from "react-bootstrap";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import './Produits.css';

export default function AjoutStock() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const typeDefaut = queryParams.get('type') || 'entree';

    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    
    const [mouvement, setMouvement] = useState({
        type: typeDefaut,
        quantite: 1,
        motif: '',
        reference: ''
    });

    useEffect(() => {
        fetchProduit();
    }, [id]);

    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (mouvement.quantite <= 0) {
            setError("La quantité doit être supérieure à 0");
            return;
        }

        if (mouvement.type === 'sortie' && mouvement.quantite > produit.stock_actuel) {
            setError(`Stock insuffisant. Stock actuel: ${produit.stock_actuel}`);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
              const userStr = localStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/mouvements_stock.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id_produit: id,
                        type: mouvement.type,
                        quantite: mouvement.quantite,
                        motif: mouvement.motif,
                        reference: mouvement.reference,
                        id_utilisateur: user?.id || user?.id_utilisateur || 1
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Mouvement enregistré avec succès !");
                setTimeout(() => {
                    navigate(`/stock/produits/${id}`);
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (!produit) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger">Produit non trouvé</Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock/produits">Produits</Link></li>
                                        <li className="breadcrumb-item"><Link to={`/stock/produits/${id}`}>Détail</Link></li>
                                        <li className="breadcrumb-item active">
                                            {mouvement.type === 'entree' ? 'Ajout au stock' : 'Retrait du stock'}
                                        </li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className={`ri-${mouvement.type === 'entree' ? 'add-box' : 'subtract'}-line text-primary me-2`}></i>
                                        {mouvement.type === 'entree' ? 'Ajouter au stock' : 'Retirer du stock'}
                                    </h4>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible>
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Row className="justify-content-center">
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-information-line me-2"></i>
                                        Produit concerné
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="product-avatar bg-primary-soft rounded-circle d-flex align-items-center justify-content-center me-3"
                                             style={{ width: '60px', height: '60px' }}>
                                            <i className="ri-suitcase-2-line text-primary fs-2"></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-1">{produit.designation}</h5>
                                            <p className="text-muted mb-0">
                                                Réf: {produit.reference} | Stock actuel: 
                                                <span className={`fw-bold ms-2 ${produit.stock_actuel <= produit.seuil_alerte ? 'text-warning' : ''}`}>
                                                    {produit.stock_actuel} {produit.unite_mesure}
                                                </span>
                                            </p>
                                        </div>
                                    </div>

                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-3">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Type de mouvement</Form.Label>
                                                    <div className="d-flex gap-4">
                                                        <Form.Check
                                                            type="radio"
                                                            id="type-entree"
                                                            label={
                                                                <span className="d-flex align-items-center">
                                                                    <i className="ri-add-box-line text-success me-2"></i>
                                                                    Entrée (ajout)
                                                                </span>
                                                            }
                                                            name="type"
                                                            value="entree"
                                                            checked={mouvement.type === 'entree'}
                                                            onChange={(e) => setMouvement({...mouvement, type: e.target.value})}
                                                        />
                                                        <Form.Check
                                                            type="radio"
                                                            id="type-sortie"
                                                            label={
                                                                <span className="d-flex align-items-center">
                                                                    <i className="ri-subtract-line text-danger me-2"></i>
                                                                    Sortie (retrait)
                                                                </span>
                                                            }
                                                            name="type"
                                                            value="sortie"
                                                            checked={mouvement.type === 'sortie'}
                                                            onChange={(e) => setMouvement({...mouvement, type: e.target.value})}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Quantité</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="1"
                                                        value={mouvement.quantite}
                                                        onChange={(e) => setMouvement({...mouvement, quantite: parseInt(e.target.value) || 1})}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>Unité</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={produit.unite_mesure}
                                                        disabled
                                                        className="bg-light"
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Référence (optionnel)</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        value={mouvement.reference}
                                                        onChange={(e) => setMouvement({...mouvement, reference: e.target.value})}
                                                        placeholder="N° BL, N° facture, etc."
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Motif / Observations</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={3}
                                                        value={mouvement.motif}
                                                        onChange={(e) => setMouvement({...mouvement, motif: e.target.value})}
                                                        placeholder="Raison du mouvement..."
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr />
                                                <div className="d-flex gap-3">
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        onClick={() => navigate(`/stock/produits/${id}`)}
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant={mouvement.type === 'entree' ? 'success' : 'warning'}
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Enregistrement...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className={`ri-${mouvement.type === 'entree' ? 'add-box' : 'subtract'}-line me-2`}></i>
                                                                Confirmer le mouvement
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm mt-4 bg-light">
                                <Card.Body>
                                    <h6 className="mb-3">
                                        <i className="ri-information-line text-primary me-2"></i>
                                        Information
                                    </h6>
                                    {mouvement.type === 'entree' ? (
                                        <p className="small text-muted mb-0">
                                            <i className="ri-check-line text-success me-2"></i>
                                            L'ajout de stock augmentera la quantité disponible. 
                                            Cette opération est généralement utilisée pour les réceptions de commandes, 
                                            les retours clients ou les inventaires positifs.
                                        </p>
                                    ) : (
                                        <p className="small text-muted mb-0">
                                            <i className="ri-alert-line text-warning me-2"></i>
                                            Le retrait de stock diminuera la quantité disponible. 
                                            Cette opération est généralement utilisée pour les ventes, 
                                            les pertes ou les inventaires négatifs.
                                        </p>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .product-avatar {
                    width: 60px;
                    height: 60px;
                    background: rgba(67, 97, 238, 0.1);
                }
                .bg-primary-soft {
                    background: rgba(67, 97, 238, 0.1);
                }
            `}</style>
        </React.Fragment>
    );
}